import { ReactElement, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal } from '@components/common/modal/modal';
import useSiteLeadStore from '@store/siteLeadStore';
import { useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';
import {
	finalizeSiteVisitRecording,
	resumeSiteVisitRecording,
} from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { BdeFeedbackDurationLimitHandler } from './BdeFeedbackDurationLimitHandler';
import { SiteVisitRecordingControlsModal } from './SiteVisitRecordingControlsModal';
import { SiteVisitRecordingDurationLimitHandler } from './SiteVisitRecordingDurationLimitHandler';
import { SiteVisitRecordingFloatingControls } from './SiteVisitRecordingFloatingControls';
import { useAutoStartSiteVisitRecording } from './useAutoStartSiteVisitRecording';
import { useRecoverSiteVisitRecordingOnFormLoad } from './useRecoverSiteVisitRecordingOnFormLoad';

interface SiteVisitRecordingContextValue {
	endRecordingOnVisitFlowComplete: () => void;
}

const SiteVisitRecordingContext = createContext<SiteVisitRecordingContextValue | null>(null);

export function useSiteVisitRecordingFlow(): SiteVisitRecordingContextValue {
	const ctx = useContext(SiteVisitRecordingContext);
	if (!ctx) {
		return { endRecordingOnVisitFlowComplete: (): void => {} };
	}
	return ctx;
}

/** Sync site id from router state when entering visit flows. Mount inside a router layout. */
export function SiteVisitRecordingRouteSync(): null {
	const recoveryComplete = useRecoverSiteVisitRecordingOnFormLoad();
	useAutoStartSiteVisitRecording(recoveryComplete);
	const { state: locationState } = useLocation();
	const { setSiteLeadId, setKeyBaseValue, siteVisitId } = useSiteLeadStore();

	const locationSiteId =
		locationState && typeof locationState === 'object' && 'siteId' in locationState
			? (locationState as { siteId?: number | null }).siteId ?? null
			: null;

	useEffect(() => {
		if (locationSiteId != null) {
			setSiteLeadId(locationSiteId);
			setKeyBaseValue('addNewSiteLead', false);
			return;
		}
		if (siteVisitId != null) {
			setKeyBaseValue('addNewSiteLead', false);
		}
	}, [locationSiteId, siteVisitId, setSiteLeadId, setKeyBaseValue]);

	return null;
}

interface SiteVisitRecordingShellProps {
	readonly children: ReactNode;
}

/**
 * App-level shell: recording controls modal + visit context.
 * Must wrap `RouterProvider` so recording UI survives all routes (dashboard, visit form, etc.).
 */
export function SiteVisitRecordingShell({ children }: Readonly<SiteVisitRecordingShellProps>): ReactElement {
	const phase = useSiteVisitRecordingStore((s) => s.phase);
	const pausedFormEditNonce = useSiteVisitRecordingStore((s) => s.pausedFormEditNonce);
	const [pauseReminderOpen, setPauseReminderOpen] = useState(false);

	const endRecordingOnVisitFlowComplete = useCallback((): void => {
		void finalizeSiteVisitRecording(true);
	}, []);

	const contextValue = useMemo(
		() => ({
			endRecordingOnVisitFlowComplete,
		}),
		[endRecordingOnVisitFlowComplete]
	);

	useEffect(() => {
		if (phase !== 'paused') {
			setPauseReminderOpen(false);
			useSiteVisitRecordingStore.getState().resetPausedFormEdit();
		}
	}, [phase]);

	useEffect(() => {
		if (phase === 'paused' && pausedFormEditNonce > 0) {
			setPauseReminderOpen(true);
		}
	}, [phase, pausedFormEditNonce]);

	const handlePauseReminderClose = useCallback((): void => {
		setPauseReminderOpen(false);
	}, []);

	const handleContinueRecording = useCallback((): void => {
		setPauseReminderOpen(false);
		resumeSiteVisitRecording().catch(() => {});
	}, []);

	useEffect(() => {
		if (phase === 'idle') {
			useSiteVisitRecordingStore.getState().setBrowserStorageUsed(null);
			useSiteVisitRecordingStore.getState().setBrowserStorageQuota(null);
			return;
		}
		const poll = async (): Promise<void> => {
			try {
				if (!navigator.storage?.estimate) {
					return;
				}
				const est = await navigator.storage.estimate();
				useSiteVisitRecordingStore.getState().setBrowserStorageUsed(est.usage ?? 0);
				useSiteVisitRecordingStore.getState().setBrowserStorageQuota(est.quota ?? 0);
			} catch {
				// ignore
			}
		};
		void poll();
		const id = window.setInterval(() => {
			void poll();
		}, 2500);
		return (): void => clearInterval(id);
	}, [phase]);

	return (
		<SiteVisitRecordingContext.Provider value={contextValue}>
			<SiteVisitRecordingFloatingControls />
			<SiteVisitRecordingControlsModal />
			<SiteVisitRecordingDurationLimitHandler />
			<BdeFeedbackDurationLimitHandler />
			<Modal
				modalOpen={pauseReminderOpen}
				modalId="siteVisitRecordingPauseReminder"
				onModalClose={handlePauseReminderClose}
				className="dialog-sm site-lead-confrim"
			>
				<div>
					<span className="h4">Do you want to stop pause and continue recording?</span>

					<div className="site-recording-button-component button-component">
						<button className="btn btn-primary" type="button" onClick={handleContinueRecording}>
							Continue
						</button>
					</div>
				</div>
			</Modal>
			{children}
		</SiteVisitRecordingContext.Provider>
	);
}

/** @deprecated Use SiteVisitRecordingShell at app root + SiteVisitRecordingRouteSync in layouts. */
export function SiteVisitRecordingProvider({ children }: Readonly<SiteVisitRecordingShellProps>): ReactElement {
	return (
		<SiteVisitRecordingShell>
			<SiteVisitRecordingRouteSync />
			{children}
		</SiteVisitRecordingShell>
	);
}
